import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/modules/user/user";
import { PhotoLayout } from "@/enums/photo-layout";

export type PhotoFilterParams = {
  date?: Date | null;
};

type GroupFilters = {
  dateFilter: Date | null;
};

/**
 * Photo filter store for managing filter state
 * Filters are set in the store and trigger automatic refetch
 * Date filters can be loaded from URL query params (for memory features)
 */
export const usePhotoFilterStore = defineStore("photoFilter", () => {
  const router = useRouter();
  const userStore = useUserStore();

  /* Layout mode preference (persisted in localStorage) */
  const layoutMode = ref<PhotoLayout>(
    (localStorage.getItem("photoLayout") as PhotoLayout) ?? PhotoLayout.List,
  );

  function setLayoutMode(mode: PhotoLayout): void {
    layoutMode.value = mode;
    localStorage.setItem("photoLayout", mode);
  }

  /* Per-group filter storage */
  const filtersByGroup = ref<Record<string, GroupFilters>>({});

  /* Computed - active group ID from user store */
  const activeGroupId = computed(() => userStore.activeGroup);

  /* Helper to ensure group exists */
  function ensureGroupFilters(groupId: string): GroupFilters {
    if (!filtersByGroup.value[groupId]) {
      filtersByGroup.value[groupId] = {
        dateFilter: null,
      };
    }
    return filtersByGroup.value[groupId]!;
  }

  /* Computed property for current group's date filter */
  const dateFilter = computed({
    get: () =>
      activeGroupId.value
        ? ensureGroupFilters(activeGroupId.value).dateFilter
        : null,
    set: (value) => {
      if (activeGroupId.value) {
        ensureGroupFilters(activeGroupId.value).dateFilter = value;
      }
    },
  });

  /* Update Date Filter */
  function setDateFilter(date: Date | null): void {
    dateFilter.value = date;
    // Don't update URL - filters are derived from URL on load
  }

  /* Clear all filters */
  function clearAllFilters(): void {
    dateFilter.value = null;
    // Don't update URL - filters are derived from URL on load
  }

  /* Check if any filters are active */
  function hasActiveFilters(): boolean {
    return dateFilter.value !== null;
  }

  /* Get current filter params for service calls */
  function getFilterParams(): PhotoFilterParams {
    return {
      date: dateFilter.value,
    };
  }

  /* Sync date filter from URL query on page load */
  function loadFiltersFromUrl(): void {
    if (!activeGroupId.value) return;

    const route = router.currentRoute.value;
    const filters = ensureGroupFilters(activeGroupId.value);

    // Load date from query params (for memory features)
    if (route.query?.date && typeof route.query.date === "string") {
      const dateObj = new Date(route.query.date);
      if (!isNaN(dateObj.getTime())) {
        filters.dateFilter = dateObj;
      }
    }
  }

  return {
    // State
    dateFilter,
    layoutMode,

    // Computed
    activeGroupId,

    // Actions
    setDateFilter,
    setLayoutMode,
    clearAllFilters,
    hasActiveFilters,
    getFilterParams,
    loadFiltersFromUrl,
  };
});
